// Validation banner — Phase 1 Section 2.8 release gate.
//
// Mounted on the Staff Dashboard above the report area. Reads the live
// validation ties from lib/reports.js and renders red (any failure) or
// green (all pass). When red, it lists every failing tie with the variance.
//
// This component is also the SOURCE OF TRUTH consulted by the Snapshot
// publish flow: the publish button is hard-disabled when allGreen === false.
// Staff CANNOT override.

import React from 'react';
import { AlertTriangle, CheckCircle2, Info } from 'lucide-react';

const formatVariance = (v) => {
  if (v === undefined || v === null || isNaN(v)) return '';
  const abs = Math.abs(v);
  const sign = v < 0 ? '-' : '';
  return `${sign}$${abs.toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
};

// Ties whose `variance` is a COUNT of failing sub-checks (not a dollar
// amount) carry a `periodChecks` array. Format them as "N sub-check(s)
// failed" and surface the specific failing sub-check name(s) so the
// user knows exactly what's wrong instead of seeing a misleading
// "variance $1".
const isCountBasedTie = (t) => Array.isArray(t.periodChecks);

const formatTieVariance = (t) => {
  if (isCountBasedTie(t)) {
    const failedCount = (t.periodChecks || []).filter(c => !c.ok).length;
    if (failedCount === 0) return '';
    return `${failedCount} check${failedCount === 1 ? '' : 's'} failed`;
  }
  if (Math.abs(t.variance || 0) <= 0.5) return '';
  return `variance ${formatVariance(t.variance)}`;
};

export default function ValidationBanner({ result, advisories = [] }) {
  if (!result) return null;
  const { ties = [], allGreen, drafts = [], publishable } = result;

  // V19.13 — Non-blocking advisories render below the main banner. They
  // never affect publishable/red status; they're purely informational
  // signals (e.g., year-end JE reconciliation differences). Filter out
  // empty/null entries defensively.
  const visibleAdvisories = (advisories || []).filter(a => a && (a.message || a.title));

  const advisoryBlock = visibleAdvisories.length > 0 ? (
    <div data-testid="validation-advisories" style={{ marginBottom: 16 }}>
      {visibleAdvisories.map((a, i) => (
        <div
          key={a.key || `advisory-${i}`}
          data-testid={`validation-advisory-${a.key || i}`}
          style={{
            padding: '10px 14px',
            background: '#EFF6FF',
            border: '1px solid #93C5FD',
            borderRadius: 8,
            fontSize: 13,
            color: '#1E3A8A',
            marginTop: i === 0 ? 0 : 8,
            display: 'flex',
            alignItems: 'flex-start',
            gap: 10,
          }}
        >
          <Info size={16} style={{ marginTop: 2, flexShrink: 0, color: '#2563EB' }} />
          <div style={{ flex: 1 }}>
            {a.title && (
              <div style={{ fontWeight: 600, marginBottom: 2 }}>{a.title}</div>
            )}
            <div style={{ lineHeight: 1.45 }}>{a.message}</div>
          </div>
        </div>
      ))}
    </div>
  ) : null;

  if (publishable) {
    return (
      <>
        <div
          data-testid="validation-banner-green"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            padding: '10px 14px',
            background: '#E8F5EE',
            border: '1px solid #1F9D57',
            borderRadius: 8,
            fontSize: 13,
            color: '#0F5A2F',
            marginBottom: 16,
          }}
        >
          <CheckCircle2 size={18} />
          <span style={{ fontWeight: 600 }}>All statements tie and are publishable.</span>
          <span style={{ color: '#1F6B3D' }}>
            Balance Sheet balances, Net Income matches equity movement, all statements canonical.
          </span>
        </div>
        {advisoryBlock}
      </>
    );
  }

  const failures = ties.filter(t => !t.ok);
  const hasFailures = failures.length > 0;
  const hasDrafts = drafts.length > 0;
  const isRed = hasFailures;

  return (
    <>
    <div
      data-testid="validation-banner-red"
      style={{
        padding: '12px 16px',
        background: isRed ? '#FEF2F2' : '#FFFBEB',
        border: `1px solid ${isRed ? '#C8322B' : '#D97706'}`,
        borderRadius: 8,
        fontSize: 13,
        color: isRed ? '#7F1D1D' : '#92400E',
        marginBottom: 16,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: hasFailures || hasDrafts ? 8 : 0 }}>
        <AlertTriangle size={18} />
        <span style={{ fontWeight: 600 }}>
          {hasFailures
            ? `${failures.length} validation tie${failures.length === 1 ? '' : 's'} failed`
            : 'Statements tie, but the package cannot be published yet'}
        </span>
        <span style={{ color: isRed ? '#9B1C1C' : '#A16207' }}>
          — Publish to client portal is disabled.
        </span>
      </div>

      {hasFailures && (
        <ul style={{ margin: '6px 0 0 28px', padding: 0, listStyle: 'disc' }}>
          {failures.map(t => {
            const variancePill = formatTieVariance(t);
            const failedChecks = isCountBasedTie(t)
              ? (t.periodChecks || []).filter(c => !c.ok)
              : [];
            return (
              <li key={t.key} data-testid={`validation-fail-${t.key}`} style={{ marginBottom: 6 }}>
                <span style={{ fontWeight: 500 }}>{t.label}</span>
                {variancePill && (
                  <span style={{ color: '#6B7280', marginLeft: 8 }}>
                    {variancePill}
                  </span>
                )}
                {t.description && (
                  <div
                    data-testid={`validation-fail-${t.key}-description`}
                    style={{
                      fontSize: 12,
                      color: '#7F1D1D',
                      marginTop: 3,
                      lineHeight: 1.45,
                      fontWeight: 400,
                    }}
                  >
                    {t.description}
                  </div>
                )}
                {failedChecks.length > 0 && (
                  <ul
                    data-testid={`validation-fail-${t.key}-checks`}
                    style={{
                      margin: '6px 0 0 16px',
                      padding: 0,
                      listStyle: 'square',
                      color: '#7F1D1D',
                      fontSize: 12,
                    }}
                  >
                    {failedChecks.map((c, i) => (
                      <li key={i} style={{ marginBottom: 2 }}>
                        <span style={{ fontWeight: 500 }}>Failing check:</span>{' '}
                        {c.name}
                        {c.detail && (
                          <span style={{ color: '#9B1C1C', marginLeft: 6, whiteSpace: 'pre-wrap' }}>
                            ({c.detail})
                          </span>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {hasDrafts && (
        <div
          data-testid="validation-drafts"
          style={{
            marginTop: hasFailures ? 10 : 6,
            padding: '8px 10px',
            background: '#FFF7ED',
            border: '1px solid #FDBA74',
            borderRadius: 6,
            color: '#7C2D12',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: 6 }}>
            <Info size={16} style={{ marginTop: 2, flexShrink: 0 }} />
            <span style={{ fontWeight: 600 }}>
              {drafts.length} statement{drafts.length === 1 ? '' : 's'} still in draft — excluded from any published package:
            </span>
          </div>
          <ul style={{ margin: '0 0 0 30px', padding: 0, listStyle: 'disc' }}>
            {drafts.map(d => (
              <li key={d.key} data-testid={`validation-draft-${d.key}`} style={{ marginBottom: 2 }}>
                {d.label}
              </li>
            ))}
          </ul>
        </div>
      )}
      </div>
      {advisoryBlock}
    </>
  );
}
